import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';

interface IPanelContext {
    placeholderColor: string | undefined;
    placeholderLogo: File | undefined;
    placeholderCover: File | undefined;
    disableExpandButtonOnHover: boolean;
    setPlaceholderColor: Dispatch<SetStateAction<string | undefined>>;
    setPlaceholderLogo: Dispatch<SetStateAction<File | undefined>>;
    setPlaceholderCover: Dispatch<SetStateAction<File | undefined>>;
    setDisableExpandButtonOnHover: Dispatch<SetStateAction<boolean>>;
}

const PanelContext = createContext<IPanelContext | undefined>(undefined);

const usePanelContext = () => {
    const context = useContext(PanelContext);
    if (!context) {
        throw new Error('usePanelContext must be used within a PanelContextProvider');
    }
    return context;
};

const PanelProvider = ({ children }: { children: ReactNode }) => {
    const [placeholderColor, setPlaceholderColor] = useState<string>();
    const [placeholderLogo, setPlaceholderLogo] = useState<File>();
    const [placeholderCover, setPlaceholderCover] = useState<File>();
    const [disableExpandButtonOnHover, setDisableExpandButtonOnHover] = useState(false);
    const value = useMemo(
        () => ({
            placeholderColor,
            placeholderLogo,
            placeholderCover,
            disableExpandButtonOnHover,
            setPlaceholderColor,
            setPlaceholderLogo,
            setPlaceholderCover,
            setDisableExpandButtonOnHover,
        }),
        [
            placeholderColor,
            placeholderLogo,
            placeholderCover,
            disableExpandButtonOnHover,
            setPlaceholderColor,
            setPlaceholderLogo,
            setPlaceholderCover,
            setDisableExpandButtonOnHover,
        ],
    );

    return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
};

export { PanelProvider, usePanelContext };
